
import { X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/custom-select";
import { Badge } from "@/components/ui/badge";

interface TagSelectorProps {
  label: string;
  selectedTags: string[];
  availableTags?: string[];
  onTagsChange: (tags: string[]) => void;
  variant?: "default" | "destructive";
  showSelectAll?: boolean;
  showNone?: boolean;
}

const DEFAULT_DIETARY_TAGS = [
  "Vegetarian",
  "Vegan",
  "Gluten-free",
  "Dairy-free",
  "Nut-free",
  "Halal",
  "Kosher",
  "Keto",
  "Paleo",
  "Low-carb",
  "Sugar-free"
];

const DEFAULT_ALLERGENS = [
  "Milk",
  "Eggs",
  "Fish",
  "Shellfish",
  "Tree nuts",
  "Peanuts",
  "Wheat",
  "Soy",
  "Sesame"
];

// Special values for select all and none
export const SELECT_ALL_VALUE = "__select_all__";
export const NONE_VALUE = "__none__";

export const TagSelector = ({
  label,
  selectedTags = [],
  availableTags,
  onTagsChange,
  variant = "default",
  showSelectAll = false,
  showNone = false
}: TagSelectorProps) => {
  const defaultTags = label.toLowerCase().includes("allergen") ? DEFAULT_ALLERGENS : DEFAULT_DIETARY_TAGS;
  const tagsToUse = availableTags?.length ? availableTags : defaultTags;

  const handleSelect = (tag: string) => {
    if (tag === SELECT_ALL_VALUE) {
      // Select all available tags
      onTagsChange([...tagsToUse]);
      return;
    }

    if (tag === NONE_VALUE) {
      // Select none (clear all) and add the NONE_VALUE
      onTagsChange([NONE_VALUE]);
      return;
    }

    // If NONE was previously selected, remove it
    let newTags = selectedTags.filter(t => t !== NONE_VALUE);
    
    // Add the new tag if it's not already there
    if (!newTags.includes(tag)) {
      onTagsChange([...newTags, tag]);
    }
  };

  const handleRemove = (tagToRemove: string) => {
    if (tagToRemove === NONE_VALUE) {
      onTagsChange([]);
      return;
    }
    
    const newTags = selectedTags.filter(tag => tag !== tagToRemove);
    // If removing the last tag, check if we should add NONE
    if (newTags.length === 0 && showNone) {
      onTagsChange([NONE_VALUE]);
    } else {
      onTagsChange(newTags);
    }
  };

  // Filter out already selected tags from available options
  let availableTagsFiltered = tagsToUse.filter(tag => !selectedTags.includes(tag));
  
  // Remove SELECT_ALL and NONE from displayed selected tags, but not from the dropdown
  const displaySelectedTags = selectedTags.filter(tag => tag !== SELECT_ALL_VALUE && tag !== NONE_VALUE);

  // Check if all tags are selected
  const allSelected = tagsToUse.every(tag => selectedTags.includes(tag));
  const noneSelected = selectedTags.includes(NONE_VALUE);

  // Special handling for display
  const selectValue = allSelected ? SELECT_ALL_VALUE : "";

  // Add special options to dropdown if needed
  if (showSelectAll && !allSelected) {
    availableTagsFiltered = [SELECT_ALL_VALUE, ...availableTagsFiltered];
  }
  
  if (showNone && !noneSelected && selectedTags.length === 0) {
    availableTagsFiltered = [NONE_VALUE, ...availableTagsFiltered];
  }

  return (
    <div className="space-y-2">
      {(availableTagsFiltered.length > 0 || noneSelected) && (
        <Select onValueChange={handleSelect} value={selectValue}>
          <SelectTrigger className="w-full bg-[#EDEDED] border-gray-200">
            <SelectValue placeholder={`Select ${label}`} />
          </SelectTrigger>
          <SelectContent className="bg-white z-50">
            {availableTagsFiltered.map((tag) => (
              <SelectItem key={tag} value={tag} className="cursor-pointer">
                {tag === SELECT_ALL_VALUE ? "Select All" : tag === NONE_VALUE ? "None" : tag}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      <div className="flex flex-wrap gap-2 min-h-[32px]">
        {noneSelected && (
          <Badge
            key="none"
            variant="default"
            className="flex items-center gap-1 px-3 py-1"
          >
            None
            <X
              className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
              onClick={() => handleRemove(NONE_VALUE)}
            />
          </Badge>
        )}

        {displaySelectedTags.map((tag) => (
          <Badge
            key={tag}
            variant={variant}
            className="flex items-center gap-1 px-3 py-1"
          >
            {tag}
            <X
              className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
              onClick={() => handleRemove(tag)}
            />
          </Badge>
        ))}

        {allSelected && showSelectAll && (
          <Badge
            key="all"
            variant="outline"
            className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 border-blue-200"
          >
            All Selected
            <X
              className="h-3 w-3 cursor-pointer hover:text-red-500 transition-colors"
              onClick={() => onTagsChange([])}
            />
          </Badge>
        )}
      </div>
    </div>
  );
};
